import React, { useEffect, useRef, useState } from 'react'
import { PRE_CHECK_MACHINE_STATES, STUDENT_PRE_CHECK_STATUS } from '../constants'
import useStudentPreChecks from '../useStudentPreChecks';
import waitForManualPermissionChange from './functions/waitForManualPermissionChange';
import ReloadPageToApplyPermission from './Components/ReloadPageToApplyPermission';
import PromptToAllowPermission from './Components/PromptToAllowPermission';
import hasAudioInputAccess, { getAudioInputAccessState } from './functions/hasAudioInputAccess';
import InstructToFixPermission from './Components/InstructToFixPermission';
import useControlledSpeech from '../Components/hooks/useControlledSpeech';
import { LOCAL_STORAGE_FLAGS } from '../constants';
import { COPY_STRINGS } from '../../../constants/translatedCopy';
import useWindowEvents from '../useWindowEvents';
import { getCopyByGradeband } from '../../../services/ThemingService';
import { getUserData } from '../../../services/API';
import { logSessionInfoBuffered } from '../../../services/logger';
import { delay, isCordova } from '../../../services/util';
import { isChromeDesktop, isEdgeDesktop, isDesktopPlatform } from '../functions/browserChecks';
import { FEATURE_FLAGS } from '../../../constants/featureFlags';
import probeImageRendering, { IMAGE_PROBE_OUTCOMES } from './functions/probeImageRendering';
import InstructToFixImagesBlocked from './Components/InstructToFixImagesBlocked';

export const MICROPHONE_STATES = {
    pending: 'pending',
    prompt: 'prompt',
    denied: 'denied',
    granted: 'granted',
    manually_granted: "manually_granted",
    // TT-1378 / CR-10713: the browser will not render images for this site, so the
    // avatar, reader panel and mic UI can never appear. Not a microphone state; it
    // lives in this check because Hardware is where browser prerequisites are verified.
    images_blocked: 'images_blocked'
}

const HARDWARE_CHECK_EVENTS = {
    MICROPHONE_PROMPT: 'MICROPHONE_PROMPT',
    MICROPHONE_DENIED: 'MICROPHONE_DENIED',
    MICROPHONE_GRANTED: 'MICROPHONE_GRANTED',
    MICROPHONE_MANUALLY_FIXED_GRANTED: 'MICROPHONE_MANUALLY_FIXED_GRANTED',
    IMAGES_BLOCKED: 'IMAGES_BLOCKED',
}

export const IMAGES_BLOCKED_ERROR = 'Images are blocked for this site in the browser'
// Gap before the confirmation probe so it samples a different moment than the first.
export const IMAGE_PROBE_CONFIRMATION_GAP_MS = 250

export default React.memo(function HardwareChecks(props) {

    /**
     * props
     */
    const { speechProps } = props

    /**
     * hook
     */
    const { speakWith } = useControlledSpeech({speechProps})
    const studentPreCheck = useStudentPreChecks({itemToCheck: PRE_CHECK_MACHINE_STATES.hardwareChecks})
    const { isCurrent, currentStatus, currentError, settings } = studentPreCheck

    /**
     * states
     */
    const [ permissionState, setPermissionState ] = useState(MICROPHONE_STATES.pending)
    const waitForPermissionChange = waitForManualPermissionChange()
    // The image probe awaits up to a few seconds; the parent can unmount this check
    // in that window (fatal exit, reset, redirect). Gate the continuation on this.
    const isMountedRef = useRef(true)

    useWindowEvents()

    useEffect(()=>{
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
            waitForPermissionChange.stop()
        }
    }, [])

    useEffect(() => {
        if (isCurrent && permissionState === MICROPHONE_STATES.pending) {
            studentPreCheck.startCheck()
            runImageRenderProbeThenHardwareChecks()
        }
    }, [isCurrent])

    /**
     * TT-1378 / CR-10713: before any microphone work, confirm the browser will render
     * images at all. When Chrome's Images setting is Block for the site, <img> fires
     * neither load nor error, so the Spine avatar init hangs forever and the static
     * fallback never appears — with no telemetry. The probe is a 1x1 inline PNG raced
     * against a timeout; healthy browsers settle in milliseconds, so this costs nothing
     * on the normal path. A first timeout is re-probed after a short gap before the
     * check fails. Blocking is gated by ENABLE_IMAGE_RENDER_PRECHECK; with the flag off
     * the probe only logs. Runs for non-verbal users too: they still need the screen.
     */
    const runImageRenderProbeThenHardwareChecks = async () => {
        // The Images content setting is a Chromium desktop feature. Elsewhere (iPad
        // Cordova, Safari, Chrome on iOS/Android) a "blocked" verdict can only be a
        // false positive and the fix instructions do not apply, so those platforms are
        // telemetry-only and skip the confirmation probe: a single sample is enough
        // for a verdict that cannot block.
        const eligibleBrowser = (isChromeDesktop() || isEdgeDesktop()) && isDesktopPlatform() && !isCordova()

        let probe = null
        try {
            const first = await probeImageRendering()
            probe = { ...first, attempts: 1 }
            const inconclusive = first.outcome === IMAGE_PROBE_OUTCOMES.timeout || first.outcome === IMAGE_PROBE_OUTCOMES.stalled
            if (inconclusive && eligibleBrowser) {
                if (!isMountedRef.current) return
                await delay(IMAGE_PROBE_CONFIRMATION_GAP_MS)
                if (!isMountedRef.current) return
                // If the confirmation throws, the unconfirmed first result must not stand.
                probe = null
                const confirmation = await probeImageRendering()
                probe = { ...confirmation, attempts: 2, firstOutcome: first.outcome, firstElapsedMs: first.elapsedMs }
                if (confirmation.outcome === IMAGE_PROBE_OUTCOMES.loaded) {
                    // The population that sizes the false-positive risk of the threshold.
                    logSessionInfoBuffered('image_render_probe_recovered', { ...probe })
                }
            }
        } catch (err) {
            probe = null
            console.error('Image render probe threw, continuing hardware checks', err)
        }

        if (!isMountedRef.current) return

        if (probe && probe.outcome === IMAGE_PROBE_OUTCOMES.errored) {
            // Unexpected (the PNG is valid and inline). Record it, do not block the student.
            logSessionInfoBuffered('image_render_probe_errored', { ...probe })
        }
        if (probe && probe.outcome === IMAGE_PROBE_OUTCOMES.stalled) {
            // Main thread was frozen through the timer; inconclusive, do not block.
            logSessionInfoBuffered('image_render_probe_stalled', { ...probe })
        }

        if (probe && probe.blocked) {
            const blocking = FEATURE_FLAGS.ENABLE_IMAGE_RENDER_PRECHECK === true && eligibleBrowser
            const details = {
                ...probe,
                blocking,
                eligibleBrowser,
                userAgent: navigator.userAgent,
                // pathname only: the query string can carry a login token and student name.
                path: window.location.pathname,
            }
            // Note: logEvent is a no-op when settings.sessionData.districtId is absent;
            // the buffered session log below does not share that dependency.
            studentPreCheck.logEvent(HARDWARE_CHECK_EVENTS.IMAGES_BLOCKED, PRE_CHECK_MACHINE_STATES.hardwareChecks, details)
            logSessionInfoBuffered('images_blocked', details)
            if (blocking) {
                setPermissionState(MICROPHONE_STATES.images_blocked)
                // failedCheck's `cause` is merged into this check's troubleshoot data by the
                // preCheckItemErrored listener, so it lands in troubleshoot_HARDWARE_CHECKS.
                // Assign `cause` after construction rather than via the Error(message, { cause })
                // option: that option needs Node >=16.9, but this repo's build/test runtime is
                // pinned to Node 14 (.nvmrc / CI), where it is silently dropped and cause is
                // undefined. Manual assignment works on Node 14 and in all browsers.
                const imagesBlockedError = new Error(IMAGES_BLOCKED_ERROR)
                imagesBlockedError.cause = { imageRenderProbe: details }
                studentPreCheck.failedCheck(imagesBlockedError)
                return
            }
            // Flag off: telemetry-only. Fall through to the normal flow.
        }

        continueHardwareChecks()
    }

    const continueHardwareChecks = () => {
        // Skip entire hardware check flow for non-verbal users
        const userData = getUserData();
        if (userData && userData.nonverbal) {
            studentPreCheck.finishCheck();
            return;
        }

        performHardwareChecks()
    }

    useEffect(()=> {
        if(permissionState === MICROPHONE_STATES.prompt){
            studentPreCheck.logEvent(HARDWARE_CHECK_EVENTS.MICROPHONE_PROMPT)
        }
        if(permissionState === MICROPHONE_STATES.granted){
            studentPreCheck.logEvent(HARDWARE_CHECK_EVENTS.MICROPHONE_GRANTED)
            waitForPermissionChange.stop()
            finishHardwareChecks()
        }
        if(permissionState === MICROPHONE_STATES.denied){
            studentPreCheck.logEvent(HARDWARE_CHECK_EVENTS.MICROPHONE_DENIED)
            waitForPermissionChange.start(setPermissionState)
        }
        if(permissionState === MICROPHONE_STATES.manually_granted){
            studentPreCheck.logEvent(HARDWARE_CHECK_EVENTS.MICROPHONE_MANUALLY_FIXED_GRANTED)
        }
    }, [permissionState])

    /**
     * methods
     */
    const finishHardwareChecks = () => {
        const handleError = () => setPermissionState(MICROPHONE_STATES.denied)
        const handleSuccess = () => studentPreCheck.finishCheck()
        speechProps.initializeRecorderIfNeeded(handleError, handleSuccess);
    }

    const performHardwareChecks = async () => {
        await speakWith({
            speak: {
                "forReturningUserWithAssessment": getCopyByGradeband(COPY_STRINGS, 'student_checks').intro_assessment_mode
            }
        })

        speechProps.abortDance();

        // micAccessState is only available on browsers supporting permission API
        const micAccessState = await getAudioInputAccessState()
        if(micAccessState){
            if(micAccessState === 'denied'){
                waitForPermissionChange.start(setPermissionState)
                setPermissionState(MICROPHONE_STATES.denied)
            }else if(micAccessState === 'prompt'){
                setPermissionState(MICROPHONE_STATES.prompt)
            }else if(micAccessState === 'granted'){
                setPermissionState(MICROPHONE_STATES.granted)
            }
        }else{
            checkAndRequestPermissionForOtherBrowsers()
        }
    }

    const checkAndRequestPermissionForOtherBrowsers = async () => {
        // if Permission API is not supported, fallback to the older userMedia API
        const hasMicAccess = await hasAudioInputAccess()
        if(hasMicAccess){
            setPermissionState(MICROPHONE_STATES.granted)
        }else{
            setPermissionState(MICROPHONE_STATES.prompt)
        }
    }


    /**
     * start rendering
     */
    if( !studentPreCheck.isCurrent ) return null

    if(permissionState === MICROPHONE_STATES.images_blocked){
        return <InstructToFixImagesBlocked
            speechProps={speechProps}
            toggleReaderContainer={props.toggleReaderContainer}
            failureReturnUrl={props.failureReturnUrl}
        />
    }

    if(permissionState === MICROPHONE_STATES.prompt){
        return <PromptToAllowPermission speechProps={speechProps} setPermissionState={setPermissionState}/>
    }

    if(permissionState === MICROPHONE_STATES.denied){
        return <InstructToFixPermission speechProps={speechProps} toggleReaderContainer={props.toggleReaderContainer}/>
    }

    if(permissionState === MICROPHONE_STATES.manually_granted){
        /**
         * removed when ready module mounted just before it starts the checks, or on beforeunload event.
         * is this reliable? if user refreshed the window
         */
        localStorage.setItem(LOCAL_STORAGE_FLAGS.READY_MODULE_SKIP_FTUE_INTRO, true)

        return <ReloadPageToApplyPermission
            speechProps={speechProps}
            toggleReaderContainer={props.toggleReaderContainer}
            failureReturnUrl={props.failureReturnUrl}
        />
    }

    if(currentStatus === STUDENT_PRE_CHECK_STATUS.failed){

        if(currentError && currentError.includes('Permission denied')){
            return<div>Provide instruction / graphic on how to fix microphone permission.</div>
        }
        return <div>Provide instruction to user to fix permission.</div>
    }
    
    return null
})